import * as React from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import clsx from 'clsx';
import { Icon, Typography } from 'components/common';
import { CypressI } from 'interfaces/cypress';

interface Navigation {
	name: string;
	label: string;
	href?: string;
	icon?: string;
	iconSrc?: string;
}

interface TabsProps {
	optionsTabs: Navigation[];
	currentOption?: string;
	setCurrentOption: (value: string) => void;
	inactiveTextClassName?: string;
}

export const Tabs: React.FC<TabsProps & CypressI> = ({
	dataCY,
	optionsTabs,
	currentOption,
	setCurrentOption,
	inactiveTextClassName = 'text-dark-40',
}) => {
	const router = useRouter();
	const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
	const activeIndex = optionsTabs.findIndex(
		(item) => item.name === currentOption
	);

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLButtonElement>,
		index: number
	): void => {
		const last = optionsTabs.length - 1;
		const targets: Record<string, number> = {
			ArrowRight: index === last ? 0 : index + 1,
			ArrowLeft: index === 0 ? last : index - 1,
			Home: 0,
			End: last,
		};
		if (!(event.key in targets)) return;
		event.preventDefault();
		tabRefs.current[targets[event.key]]?.focus();
	};

	return (
		<div className="overflow-x-auto overflow-y-hidden border-b border-gray-200">
			<nav data-cy={dataCY} className="flex" aria-label="Tabs">
				<div
					role="tablist"
					aria-label="Tabs"
					className="flex gap-x-4 items-center w-full"
				>
					{optionsTabs.map((item, index) => {
						const isActive = currentOption === item.name;
						const isTabStop = isActive || (activeIndex === -1 && index === 0);

						return (
							<button
								data-cy={`tab-option-${dataCY}`}
								type="button"
								key={item.name}
								ref={(node) => {
									tabRefs.current[index] = node;
								}}
								id={`tab-${item.name}`}
								role="tab"
								aria-selected={isActive}
								tabIndex={isTabStop ? 0 : -1}
								onKeyDown={(event) => handleKeyDown(event, index)}
								onClick={() => {
									if (item.href) {
										router.push(item.href);
									} else {
										setCurrentOption(item.name);
									}
								}}
								className={clsx(
									'ring-transparent pb-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
									{
										'border-b-2 border-primary': isActive,
									}
								)}
							>
								<Typography
									type="link-1"
									className={clsx(
										'flex items-center gap-2',
										isActive ? 'text-primary' : inactiveTextClassName
									)}
								>
									{item.iconSrc ? (
										<Image
											src={item.iconSrc}
											alt=""
											width={16}
											height={16}
											className={clsx('shrink-0', {
												'opacity-60': !isActive,
											})}
										/>
									) : (
										item.icon && (
											<Icon
												src={item.icon}
												width={16}
												height={16}
												fillGrayPath={!isActive}
											/>
										)
									)}
									{item.label}
								</Typography>
							</button>
						);
					})}
				</div>
			</nav>
		</div>
	);
};
